import { useEventUserRole } from '@shared/components/AuthProvider';
import { EventUserStatus } from '@graphql/generated-base';
import { useEventScopedCoupleRoleQuery } from './useEventScopedCoupleRole.graphql.generated';

type UseEventScopedCoupleRoleArgs = Readonly<{
  /** Event to resolve the role for — NOT the auth session's active event. */
  eventId: string | undefined;
}>;

type EventScopedCoupleRole = Readonly<{
  /** True when the logged-in user is owner/admin (either partner) of THIS event. */
  isCouple: boolean;
  isLoading: boolean;
  /**
   * The membership lookup failed, so `isCouple: false` is unknown rather than "not the couple".
   * Callers hiding something from the couple should treat this as "might be the couple".
   */
  hasError: boolean;
}>;

/**
 * Resolves the couple role from the event's real membership row (`scopedActiveEventUserForEvent`).
 * Unlike `useEventUserRole`, this works on non-event routes (`/travel/checkout`, reservation
 * status) and is not masked by the account-level superAdmin flag: staff pass only on events
 * they actually own (their row says `owner`), and get no row at all on anyone else's event.
 */
export const useEventScopedCoupleRole = ({ eventId }: UseEventScopedCoupleRoleArgs): EventScopedCoupleRole => {
  const { isLoggedIn, fetching: isAuthLoading } = useEventUserRole();

  const { data, loading, error } = useEventScopedCoupleRoleQuery({
    batchMode: 'fast',
    variables: { eventId: eventId ?? '' },
    skip: !isLoggedIn || !eventId,
  });

  const scopedStatus = data?.me?.scopedActiveEventUserForEvent?.status;
  const isCouple = scopedStatus === EventUserStatus.owner || scopedStatus === EventUserStatus.admin;
  // Same condition as `skip`: Apollo keeps the last error after the query starts skipping.
  const isQueryActive = isLoggedIn && !!eventId;

  return {
    isCouple,
    isLoading: isAuthLoading || (isQueryActive && loading),
    hasError: isQueryActive && Boolean(error),
  };
};
